/**
 * Column chooser and saved views for the catalog.
 *
 * Both replace a fixed "Key columns / All columns" switch, which was two
 * opinions about what matters imposed on everybody. A buyer cares about owner
 * and project; a toolmaker cares about core material and shrinkage. Neither
 * list is wrong, so neither is hard-coded.
 */
import { useState, type ReactNode } from 'react';
import { Bookmark, Check, Columns3, Trash2 } from 'lucide-react';
import { DropdownMenu } from 'radix-ui';
import { Button } from '../../components/ui/button.js';
import { Input } from '../../components/ui/field.js';
import { cn } from '../../lib/cn.js';
import { DEFAULT_COLUMNS, type SavedView } from './catalogViews.js';

/** A menu that stays open while you tick things. Radix closes on select by default. */
function StickyMenu({
  trigger,
  children,
  align = 'end',
}: {
  trigger: ReactNode;
  children: ReactNode;
  align?: 'start' | 'end';
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align={align}
          sideOffset={6}
          className="pop z-50 max-h-[70vh] w-64 overflow-y-auto rounded-lg border border-border bg-surface-raised p-1 shadow-lg"
        >
          {children}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export function ColumnChooser({
  columns,
  selected,
  onChange,
  className,
}: {
  columns: Array<{ key: string; header: string }>;
  selected: string[];
  onChange: (next: string[]) => void;
  className?: string;
}) {
  const chosen = new Set(selected);

  function toggle(key: string) {
    const next = new Set(chosen);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    // Keep the spreadsheet's order regardless of the order things were ticked.
    onChange(columns.filter((c) => next.has(c.key)).map((c) => c.key));
  }

  return (
    <StickyMenu
      trigger={
        <Button variant="secondary" className={className} title="Choose which columns to show">
          <Columns3 className="size-4" />
          <span className="hidden xl:inline">Columns</span>
          <span className="text-xs text-muted-foreground">
            {chosen.size}/{columns.length}
          </span>
        </Button>
      }
    >
      <div className="flex items-center justify-between px-2 py-1.5">
        <span className="text-xs font-semibold text-muted-foreground">Columns</span>
        <div className="flex gap-1">
          <button
            type="button"
            className="link-btn text-xs text-primary hover:underline"
            onClick={() => onChange(columns.map((c) => c.key))}
          >
            All
          </button>
          <span className="text-xs text-subtle-foreground">·</span>
          <button
            type="button"
            className="link-btn text-xs text-primary hover:underline"
            onClick={() => onChange(DEFAULT_COLUMNS)}
          >
            Reset
          </button>
        </div>
      </div>
      {columns.map((c) => (
        <DropdownMenu.Item
          key={c.key}
          // Without this the menu shuts after one tick and choosing six columns
          // means opening it six times.
          onSelect={(e) => {
            e.preventDefault();
            toggle(c.key);
          }}
          className="flex cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-surface-sunken"
        >
          <span
            className={cn(
              'grid size-4 shrink-0 place-items-center rounded border',
              chosen.has(c.key) ? 'border-primary bg-primary text-white' : 'border-border-strong',
            )}
          >
            {chosen.has(c.key) ? <Check className="size-3" strokeWidth={3} /> : null}
          </span>
          <span className="min-w-0 flex-1 truncate">{c.header || '(image)'}</span>
        </DropdownMenu.Item>
      ))}
    </StickyMenu>
  );
}

export function ViewMenu({
  views,
  dirty,
  onApply,
  onSave,
  onDelete,
  className,
}: {
  views: SavedView[];
  /** True when a search or filter is active - i.e. there is something to save. */
  dirty: boolean;
  onApply: (view: SavedView) => void;
  onSave: (name: string) => void;
  onDelete: (id: string) => void;
  className?: string;
}) {
  const [name, setName] = useState('');

  return (
    <StickyMenu
      trigger={
        <Button variant="secondary" className={className} title="Saved searches and filters">
          <Bookmark className="size-4" />
          <span className="hidden xl:inline">Views</span>
          {views.length ? <span className="text-xs text-muted-foreground">{views.length}</span> : null}
        </Button>
      }
    >
      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">Saved views</div>

      {views.length === 0 ? (
        <p className="px-2 pb-2 text-sm text-muted-foreground">
          None yet. Filter the list, then save it here.
        </p>
      ) : (
        views.map((v) => (
          <DropdownMenu.Item
            key={v.id}
            onSelect={() => onApply(v)}
            className="group flex cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-surface-sunken"
          >
            <Bookmark className="size-3.5 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate">{v.name}</span>
            <button
              type="button"
              aria-label={`Delete the view ${v.name}`}
              className="shrink-0 rounded p-1 text-muted-foreground opacity-0 hover:text-danger group-hover:opacity-100"
              onClick={(e) => {
                // Without this the click also selects the item and applies the
                // view we are in the middle of deleting.
                e.stopPropagation();
                onDelete(v.id);
              }}
            >
              <Trash2 className="size-3.5" />
            </button>
          </DropdownMenu.Item>
        ))
      )}

      <div className="mt-1 border-t border-border p-2">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) return;
            onSave(name);
            setName('');
          }}
          className="flex gap-1.5"
        >
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={dirty ? 'Name this view' : 'Nothing filtered yet'}
            className="h-8 text-sm"
            onKeyDown={(e) => e.stopPropagation()} // let people type "d" without Radix typeahead stealing it
          />
          <Button type="submit" size="sm" variant="secondary" disabled={!name.trim()}>
            Save
          </Button>
        </form>
      </div>
    </StickyMenu>
  );
}
